import { apiFetch } from '@/api/apiFetch';
import { EProductFormat } from '@/domain/product/EProductFormat';
import { Product } from '@/domain/product/Product';

interface IProductApiResponse {
    cuttingPath: string | null;
    doubleSided: boolean;
    height: number;
    helpGridRecto: string | null;
    helpGridVerso: string | null;
    id: string;
    isColorsLocked: boolean;
    label: string;
    meshBaseName: string | null;
    model: string | null;
    name: string;
    productType: '2D' | '3D';
    width: number;
}

interface IProductListApiResponse {
    items: IProductApiResponse[];
    limit: number;
    page: number;
    total: number;
}

interface IProductListResult {
    items: Product[];
    limit: number;
    page: number;
    total: number;
}

interface IPatchProductDto {
    cuttingPath?: string;
    doubleSided?: boolean;
    height?: number;
    helpGridRecto?: string;
    helpGridVerso?: string;
    label?: string;
    width?: number;
}

interface IPostProductDto {
    doubleSided: boolean;
    height: number;
    isColorsLocked: boolean;
    label: string;
    name: string;
    productType: '2D' | '3D';
    width: number;
}

// Same concern as thumbnailFileIdToUrl in templateMapper: prefixing VITE_API_URL belongs with
// apiFetch, not in a component.
function productFileUrl(fileId: string): string {
    return `${import.meta.env.VITE_API_URL}/product-files/${fileId}`;
}

function mapToProduct(data: IProductApiResponse): Product {
    return new Product({
        id: data.id,
        name: data.name,
        label: data.label,
        width: data.width,
        height: data.height,
        isColorsLocked: data.isColorsLocked,
        format: data.doubleSided ? EProductFormat.RECTO_VERSO : EProductFormat.RECTO,
        has3d: data.productType === '3D',
        helpGridRecto: data.helpGridRecto,
        helpGridVerso: data.helpGridVerso,
        cuttingPath: data.cuttingPath,
        model: data.model,
        meshBaseName: data.meshBaseName,
    });
}

async function getProduct(productType: string): Promise<Product> {
    const response = await apiFetch(`/products/${productType}`);
    const data: IProductApiResponse = await response.json();
    return mapToProduct(data);
}

async function getProducts(page = 1, limit = 10): Promise<IProductListResult> {
    const response = await apiFetch(`/products?page=${page}&limit=${limit}`);
    const data: IProductListApiResponse = await response.json();
    return {
        items: data.items.map(mapToProduct),
        total: data.total,
        page: data.page,
        limit: data.limit,
    };
}

async function patchProduct(name: string, dto: IPatchProductDto): Promise<Product> {
    const response = await apiFetch(
        `/products/${name}`,
        {
            method: 'PATCH',
            body: JSON.stringify(dto),
        },
        [['Content-Type', 'application/json']],
    );
    const data: IProductApiResponse = await response.json();
    return mapToProduct(data);
}

async function postProduct(dto: IPostProductDto): Promise<Product> {
    const response = await apiFetch(
        '/products',
        {
            method: 'POST',
            body: JSON.stringify(dto),
        },
        [['Content-Type', 'application/json']],
    );
    const data: IProductApiResponse = await response.json();
    return mapToProduct(data);
}

async function deleteProduct(name: string): Promise<void> {
    await apiFetch(`/products/${name}`, {
        method: 'DELETE',
    });
}

export { deleteProduct, getProduct, getProducts, patchProduct, postProduct, productFileUrl };
